/* ===========================================================
   CHECKOUT — 3-step wizard (address → payment → review)
   Wired to useCart / useAddresses / useCheckoutQuote / usePlaceOrder.
   =========================================================== */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ProductTile } from "@/components/product-card";
import { Breadcrumbs, toast } from "@/components/shell";
import { EmptyState } from "@/components/EmptyState";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { inr } from "@/data/data";
import { Icons } from "@/lib/icons";
import {
  useCart,
  useAddresses,
  useCreateAddress,
  useCheckoutQuote,
  usePlaceOrder,
} from "@/api/hooks";

const STEPS = [
  { k: "address", label: "Address" },
  { k: "payment", label: "Payment" },
  { k: "review",  label: "Review" },
];

function Stepper({ active }) {
  return (
    <div className="stepper">
      {STEPS.map((s, i) => {
        const idx = STEPS.findIndex((x) => x.k === active);
        const cls = i < idx ? "is-done" : i === idx ? "is-active" : "";
        return (
          <React.Fragment key={s.k}>
            <span className={"step " + cls}>
              <span className="step-num">
                {i < idx ? <Icons.Check size={12} stroke={3} /> : i + 1}
              </span>
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="step-sep" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function OrderSummaryCard({ cart, deliveryMethod }) {
  if (!cart) return null;
  let shippingOverride = cart.shipping;
  if (deliveryMethod === "express") shippingOverride = 150;
  else if (deliveryMethod === "cold_chain") shippingOverride = 320;
  const total = Math.max(0, cart.subtotal + cart.gst_amount + shippingOverride - cart.discount);

  return (
    <aside className="cart-summary">
      <div className="cart-summary-h">Order summary</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14, paddingBottom: 14, borderBottom: "1px dashed var(--line)" }}>
        {cart.items.map((it) => (
          <div key={it.id} style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 44, height: 44, position: "relative" }}>
              <ProductTile product={{ name: it.product_name, slug: it.product_slug }} size="sm" />
              <span style={{ position: "absolute", top: -6, right: -6, background: "var(--ink)", color: "#fff", fontSize: 10, fontWeight: 600, width: 18, height: 18, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center" }}>{it.qty}</span>
            </div>
            <div style={{ flex: 1, fontSize: 12.5 }}>
              <div style={{ fontWeight: 500, color: "var(--ink)" }}>{it.product_name}</div>
              <div style={{ color: "var(--ink-3)" }}>{it.product_unit_label}</div>
            </div>
            <div className="mono" style={{ fontSize: 13, fontWeight: 500 }}>{inr(it.line_total)}</div>
          </div>
        ))}
      </div>
      <div className="cart-summary-row"><span>Subtotal</span><span className="mono">{inr(cart.subtotal)}</span></div>
      {cart.coupon && (
        <div className="cart-summary-row" style={{ color: "#0a0a0a" }}>
          <span>Discount ({cart.coupon.code})</span><span className="mono">−{inr(cart.discount)}</span>
        </div>
      )}
      <div className="cart-summary-row"><span>GST (5%)</span><span className="mono">{inr(cart.gst_amount)}</span></div>
      <div className="cart-summary-row">
        <span>Shipping</span>
        <span className="mono">{shippingOverride === 0 ? "FREE" : inr(shippingOverride)}</span>
      </div>
      <div className="cart-summary-row total"><span>Total</span><span className="mono">{inr(total)}</span></div>
      <div className="cart-trust">
        <div className="cart-trust-item"><Icons.ShieldCheck size={14} stroke={1.8} /> Secure checkout · SSL encrypted</div>
        <div className="cart-trust-item"><Icons.BadgeCheck size={14} stroke={1.8} /> CDSCO-licensed wholesaler</div>
      </div>
    </aside>
  );
}

function AddressStep({ data, setData, onNext }) {
  const { data: addresses = [], isLoading } = useAddresses();
  const createAddress = useCreateAddress();
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ label: "", phone: "", line1: "", line2: "", city: "", state: "MH", pin: "" });

  // Auto-select default address on first load.
  React.useEffect(() => {
    if (!data.addressId && addresses.length > 0) {
      const def = addresses.find((a) => a.default) || addresses[0];
      setData((d) => ({ ...d, addressId: def.id }));
    }
  }, [addresses, data.addressId, setData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.label || !form.line1 || !form.city || !form.pin || !form.phone) {
      toast && toast("Fill in all required fields", "error");
      return;
    }
    createAddress.mutate(form, {
      onSuccess: (created) => {
        toast && toast("Address saved");
        setShowForm(false);
        setData((d) => ({ ...d, addressId: created.id }));
        setForm({ label: "", phone: "", line1: "", line2: "", city: "", state: "MH", pin: "" });
      },
    });
  };

  return (
    <div className="checkout-card">
      <div className="checkout-card-head">
        <div className="checkout-card-title">Shipping address</div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowForm((s) => !s)}>
          <Icons.Plus size={13} stroke={2} /> {showForm ? "Cancel" : "Add new"}
        </button>
      </div>

      {isLoading ? (
        <LoadingSkeleton rows={2} height={64} />
      ) : !showForm ? (
        addresses.length === 0 ? (
          <EmptyState
            title="No saved addresses"
            description="Add a delivery address to continue checkout."
            cta={{ label: "Add address", onClick: () => setShowForm(true) }}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {addresses.map((a) => (
              <button
                key={a.id}
                type="button"
                className={"addr-card" + (data.addressId === a.id ? " is-selected" : "")}
                onClick={() => setData((d) => ({ ...d, addressId: a.id }))}
              >
                <span className="addr-radio" />
                <div className="addr-card-body">
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div className="addr-card-name">{a.label}</div>
                    {a.type && <span className="pill pill-brand">{a.type}</span>}
                    {a.default && <span className="pill pill-outline">Default</span>}
                  </div>
                  <div className="addr-card-lines">
                    {a.line1}{a.line2 ? ", " + a.line2 : ""}, {a.city} {a.pin}, {a.state}
                  </div>
                  <div className="addr-card-phone mono">{a.phone}</div>
                </div>
              </button>
            ))}
          </div>
        )
      ) : (
        <form className="form-grid" onSubmit={handleSubmit}>
          <div className="field"><label className="field-label">Label *</label><input className="input" placeholder="Pharmacy — Branch 2" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></div>
          <div className="field"><label className="field-label">Phone *</label><input className="input" type="tel" placeholder="+91" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="field full"><label className="field-label">Address line 1 *</label><input className="input" value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} /></div>
          <div className="field full"><label className="field-label">Address line 2 (optional)</label><input className="input" value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} /></div>
          <div className="field"><label className="field-label">City *</label><input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          <div className="field"><label className="field-label">PIN code *</label><input className="input" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} /></div>
          <div className="field full" style={{ flexDirection: "row", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={createAddress.isPending}>
              {createAddress.isPending ? "Saving…" : "Save address"}
            </button>
          </div>
        </form>
      )}

      <div className="checkout-actions">
        <a href="/pages/storefront/cart.html" className="btn btn-ghost"><Icons.ChevL size={14} /> Back to cart</a>
        <button className="btn btn-primary" onClick={onNext} disabled={!data.addressId}>
          Continue to payment <Icons.ArrowR size={14} stroke={2} />
        </button>
      </div>
    </div>
  );
}

function PaymentStep({ data, setData, onNext, onBack }) {
  const methods = [
    { k: "cod",     label: "Cash on delivery",         icon: "Truck" },
    { k: "upi",     label: "UPI",                       icon: "Hash" },
    { k: "card",    label: "Credit / Debit card",       icon: "BadgeCheck" },
    { k: "netbank", label: "Net Banking",               icon: "Building" },
    { k: "credit",  label: "Retailer credit (Net 30)",  icon: "Calendar" },
  ];
  const deliveries = [
    { k: "standard",    label: "Standard",    sub: "2–4 days · free over ₹2k", price: 0 },
    { k: "express",     label: "Express",     sub: "1–2 days · ₹150",          price: 150 },
    { k: "cold_chain",  label: "Cold-chain",  sub: "2–8°C lane · ₹320",        price: 320 },
  ];

  return (
    <div className="checkout-card">
      <div className="checkout-card-head">
        <div className="checkout-card-title">Payment & delivery</div>
        <span className="kicker"><Icons.ShieldCheck size={11} stroke={1.8} /> SECURE · TLS 1.3</span>
      </div>

      <div className="kicker" style={{ marginTop: 8, marginBottom: 8 }}>Delivery method</div>
      <div className="pay-grid" style={{ marginBottom: 18 }}>
        {deliveries.map((o) => (
          <button
            key={o.k}
            type="button"
            className={"pay-card" + (data.delivery === o.k ? " is-selected" : "")}
            onClick={() => setData((d) => ({ ...d, delivery: o.k }))}
          >
            <div style={{ fontWeight: 500 }}>{o.label}</div>
            <div className="kicker" style={{ marginTop: 4 }}>{o.sub}</div>
          </button>
        ))}
      </div>

      <div className="kicker" style={{ marginTop: 8, marginBottom: 8 }}>Payment method</div>
      <div className="pay-grid">
        {methods.map((m) => {
          const Icon = Icons[m.icon];
          return (
            <button
              key={m.k}
              type="button"
              className={"pay-card" + (data.payment === m.k ? " is-selected" : "")}
              onClick={() => setData((d) => ({ ...d, payment: m.k }))}
            >
              <div className="pay-card-ic"><Icon size={18} stroke={1.7} /></div>
              {m.label}
            </button>
          );
        })}
      </div>

      {data.payment === "upi" && (
        <div className="field" style={{ marginTop: 18 }}>
          <label className="field-label">UPI ID</label>
          <input className="input" placeholder="you@bank" />
          <div className="kicker" style={{ marginTop: 6 }}>Mock — not actually charged.</div>
        </div>
      )}
      {data.payment === "card" && (
        <div className="form-grid" style={{ marginTop: 18 }}>
          <div className="field full"><label className="field-label">Card number</label><input className="input" placeholder="1234 5678 9012 3456" /></div>
          <div className="field"><label className="field-label">Expiry</label><input className="input" placeholder="MM / YY" /></div>
          <div className="field"><label className="field-label">CVV</label><input className="input" type="password" placeholder="•••" /></div>
        </div>
      )}
      {data.payment === "netbank" && (
        <div className="field" style={{ marginTop: 18 }}>
          <label className="field-label">Bank</label>
          <select className="input"><option>HDFC Bank</option><option>State Bank of India</option><option>ICICI Bank</option><option>Axis Bank</option></select>
        </div>
      )}
      {data.payment === "credit" && (
        <div className="pdp-trust-row" style={{ marginTop: 18 }}>
          <div className="pdp-trust-item">
            <Icons.Calendar size={18} stroke={1.6} />
            <div>
              <strong>Net 30 retailer terms</strong>
              <span>Statement on 1st of next month · Credit ceiling ₹4,80,000</span>
            </div>
          </div>
        </div>
      )}

      <div className="checkout-actions">
        <button className="btn btn-ghost" onClick={onBack}><Icons.ChevL size={14} /> Back</button>
        <button className="btn btn-primary" onClick={onNext}>Review order <Icons.ArrowR size={14} stroke={2} /></button>
      </div>
    </div>
  );
}

function ReviewStep({ data, addresses, cart, onBack, onPlace, isPlacing }) {
  const addr = addresses.find((a) => a.id === data.addressId);
  const deliveryLabel =
    data.delivery === "express" ? "Express dispatch" :
    data.delivery === "cold_chain" ? "Cold-chain lane" : "Standard delivery";
  const deliverySub =
    data.delivery === "express" ? "1–2 business days" :
    data.delivery === "cold_chain" ? "1–3 business days · 2–8°C" : "2–4 business days";
  const paymentLabel = {
    cod: "Cash on delivery", upi: "UPI", card: "Card",
    netbank: "Net Banking", credit: "Retailer credit (Net 30)",
  }[data.payment] || data.payment;

  return (
    <div className="checkout-card">
      <div className="checkout-card-head">
        <div className="checkout-card-title">Review & place order</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        <div>
          <div className="kicker" style={{ marginBottom: 8 }}>Ship to</div>
          <div className="addr-card is-selected" style={{ cursor: "default" }}>
            <span className="addr-radio" />
            <div className="addr-card-body">
              <div className="addr-card-name">{addr?.label}</div>
              <div className="addr-card-lines">
                {addr?.line1}{addr?.line2 ? ", " + addr.line2 : ""}, {addr?.city} {addr?.pin}
              </div>
              <div className="addr-card-phone mono">{addr?.phone}</div>
            </div>
          </div>
        </div>
        <div>
          <div className="kicker" style={{ marginBottom: 8 }}>Delivery & payment</div>
          <div style={{ padding: 16, border: "1px solid var(--line)", borderRadius: 10, fontSize: 13.5, lineHeight: 1.7 }}>
            <div><strong>{deliveryLabel}</strong></div>
            <div style={{ color: "var(--ink-3)" }}>{deliverySub}</div>
            <div style={{ marginTop: 10, borderTop: "1px dashed var(--line)", paddingTop: 10 }}>
              <strong>{paymentLabel}</strong>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="kicker" style={{ marginBottom: 8 }}>Items ({cart.items.length})</div>
        <div style={{ border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden" }}>
          {cart.items.map((it, i) => (
            <div
              key={it.id}
              style={{ display: "flex", gap: 14, alignItems: "center", padding: 14, borderBottom: i < cart.items.length - 1 ? "1px solid var(--line)" : "none" }}
            >
              <div style={{ width: 44, height: 44 }}><ProductTile product={{ name: it.product_name, slug: it.product_slug }} size="sm" /></div>
              <div style={{ flex: 1, fontSize: 13 }}>
                <div style={{ fontWeight: 500 }}>{it.product_name}</div>
                <div style={{ color: "var(--ink-3)" }}>Qty {it.qty} · {it.product_unit_label}</div>
              </div>
              <div className="mono" style={{ fontWeight: 500 }}>{inr(it.line_total)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="checkout-actions">
        <button className="btn btn-ghost" onClick={onBack}><Icons.ChevL size={14} /> Back to payment</button>
        <button className="btn btn-primary btn-lg" onClick={onPlace} disabled={isPlacing}>
          {isPlacing ? "Placing…" : <>Place order <Icons.ShieldCheck size={15} stroke={2} /></>}
        </button>
      </div>
    </div>
  );
}

function CheckoutPage() {
  const navigate = useNavigate();
  const { data: cart, isLoading: cartLoading } = useCart();
  const { data: addresses = [] } = useAddresses();
  const placeOrder = usePlaceOrder();
  const [step, setStep] = React.useState("address");
  const [data, setData] = React.useState({
    addressId: "",
    delivery: "standard",
    payment: "cod",
  });

  const go = (next) => {
    setStep(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePlace = () => {
    placeOrder.mutate(
      {
        address_id: data.addressId,
        delivery_method: data.delivery,
        payment_method: data.payment,
      },
      {
        onSuccess: (order) => {
          toast && toast("Order placed");
          navigate("/pages/storefront/confirmation.html?order=" + encodeURIComponent(order.number));
        },
        onError: (err) => toast && toast(err?.message || "Couldn't place order", "error"),
      }
    );
  };

  if (cartLoading) {
    return (
      <>
        <Breadcrumbs items={[{ label: "Cart", href: "/pages/storefront/cart.html" }, { label: "Checkout" }]} />
        <div className="container" style={{ padding: "32px 0" }}>
          <LoadingSkeleton rows={6} height={48} />
        </div>
      </>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <>
        <Breadcrumbs items={[{ label: "Cart", href: "/pages/storefront/cart.html" }, { label: "Checkout" }]} />
        <div className="container" style={{ padding: "32px 0" }}>
          <EmptyState
            title="Your cart is empty"
            description="Add items to your cart before checking out."
            cta={{ label: "Browse products", href: "/pages/storefront/products.html" }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs items={[
        { label: "Cart", href: "/pages/storefront/cart.html" },
        { label: "Checkout" },
      ]} />
      <div className="container">
        <header style={{ padding: "32px 0 0" }}>
          <h1 style={{ fontSize: "clamp(28px, 3.6vw, 40px)", letterSpacing: "-0.025em" }}>Checkout</h1>
        </header>
        <div className="checkout-layout">
          <div>
            <Stepper active={step} />
            {step === "address" && (
              <AddressStep data={data} setData={setData} onNext={() => go("payment")} />
            )}
            {step === "payment" && (
              <PaymentStep
                data={data}
                setData={setData}
                onNext={() => go("review")}
                onBack={() => go("address")}
              />
            )}
            {step === "review" && (
              <ReviewStep
                data={data}
                addresses={addresses}
                cart={cart}
                onBack={() => go("payment")}
                onPlace={handlePlace}
                isPlacing={placeOrder.isPending}
              />
            )}
          </div>
          <OrderSummaryCard cart={cart} deliveryMethod={data.delivery} />
        </div>
      </div>
    </>
  );
}

export { CheckoutPage };
